/**
 * ============================================================================
 * METRIC TABLE SECTION — the core reusable unit of the dashboard.
 * ============================================================================
 *
 * This is the highest-complexity component — it IS the dashboard.
 * Everything in the Figma's tab content is instances of this.
 *
 * SHAPE (from Figma): a collapsible section containing:
 *   ▾ {title}  ⓘ                                      ‹ 1 2 3 4 5 › ⚙
 *   [🔍 Find resources]  [Period: Historic period ▾]
 *   ┌─Metric──────────────┬─2026 W26─┬─2026 W27─┬─2026 W28─┬─…─┬─Callout─┐
 *   │ OTD % vs Requested  │ 🔴 42.8% │ 🔴 43.2% │ 🔴 43.1% │   │   💬    │
 *   │ …one row per metric │          │          │          │   │         │
 *   └─────────────────────┴──────────┴──────────┴───────────┴───┴─────────┘
 *   Week columns are grouped: 1. Historical | 2. Current | 3. Forecasted.
 *
 * STATE RULES (critical — do not "fix" this into Redux):
 * - period / search / page are LOCAL useState. The Figma shows two sections
 *   with different Period selections simultaneously — they are independent.
 * - Row data: buildDcrPredicate(filterQuery) filters DCRs from the cached
 *   getMetrics response, then selectMetricTableRows pivots metric×week for
 *   THIS section's scope.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { CellClickedEvent, ColDef, ColGroupDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import ExpandableSection from '@amzn/awsui-components-core/expandable-section';
import Input from '@amzn/awsui-components-core/input';
import Pagination from '@amzn/awsui-components-core/pagination';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Link from '@amzn/awsui-components-core/link';
import Header from '@amzn/awsui-components-core/header';
import Button from '@amzn/awsui-components-core/button';
import Textarea from '@amzn/awsui-components-core/textarea';
import Box from '@amzn/awsui-components-core/box';
import { MetricTableSectionConfig } from 'src/types';
import { useAppSelector, useAppDispatch } from 'src/app/store';
import { filterQueryChanged } from 'src/features/dashboard/dashboardSlice';
import { useMetricsWindow } from 'src/features/api/useMetricsWindow';
import {
  selectDslCoverageRows,
  selectMetricTableRows,
  selectMetricTrend,
  selectWeekColumns,
  MetricTableRow,
  PeriodGroup,
  PERIOD_GROUP_LABELS,
  OTD_THRESHOLD_DEFAULT,
  WeekColumn,
} from 'src/features/api/tableSelectors';
import { WBR_DASHBOARD_ID, WBR_REVIEW_TYPE } from 'src/constants';
import { buildDcrPredicate, selectedMetricGroups } from 'src/features/api/filterPredicate';
import { buildDurationWindow, weekBefore } from 'src/features/api/durationRange';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import { demandCategoryDisplayName } from 'src/config/demandCategories';
import { TrendCell, MetricDelta } from 'src/components/table/TrendCell';
import { ExportMenu } from 'src/components/common/ExportMenu';
import { ExportColumn } from 'src/components/common/exportTable';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

interface MetricTableSectionProps {
  config: MetricTableSectionConfig;
  /** Restrict which metric kinds this section shows (config-driven). */
  metricTypes?: Array<'percentage' | 'volume' | 'count'>;
  /** Snapshot mode (WBR Snapshot tab) vs live. */
  isSnapshot?: boolean;
  /**
   * Whether the Metric Group chips scope this section. True for the volume
   * summary tables; FALSE for the On Time Delivery % tables, which have fixed
   * rows in the live dashboard and ignore the control.
   */
  respectsMetricGroup?: boolean;
  /**
   * Per-row Owner/Goal columns (QuickSight summary-table style). Sections
   * whose metrics share one owner (OTD) omit them — the section header
   * carries Owner/Goal instead.
   */
  showOwnerColumns?: boolean;
  /** Trend sparkline + Δ WoW columns (spec: executive-summary-trends). Default on. */
  showTrend?: boolean;
  /**
   * Network scope override (spec R10): the Detailed Summary tab renders one
   * section group per CHECKED network, so scoping can't come from the global
   * activeNetwork. Absent → falls back to activeNetwork (other pages).
   */
  network?: string;
  /**
   * Where this section's rows come from (spec dsl-coverage-snapshot):
   * - 'catalog' (default): metric×week pivot of catalog metrics present in
   *   the data (selectMetricTableRows).
   * - 'dslCoverage': the two derived DSL rows from DcrRecord.collectionOwner
   *   (selectDslCoverageRows) — no catalog join, metricTypes/metricGroups
   *   don't apply.
   */
  rowSource?: 'catalog' | 'dslCoverage';
}

// Configured sections have ≤6 rows (OTD = 3 network rows + 3 derived %DCRs
// rows); a size of 5 hid the last OTD row behind a pagination click.
const ROWS_PER_PAGE = 10;

/**
 * Rate goals format as percentages ("≥ 95%"), volume goals as plain numbers
 * ("≥ 1,000"); direction from the catalog's goalDirection (LTE → "≤").
 * Shared by the metric cell and the export column so they never drift.
 */
function formatGoal(row: MetricTableRow): string {
  if (!(row.goal > 0)) return 'N/A';
  const comparator = row.goalDirection === 'LTE' ? '≤' : '≥';
  return row.isRate
    ? `${comparator} ${row.goal}%`
    : `${comparator} ${Number(row.goal).toLocaleString()}`;
}

interface MetricNameCellParams extends ICellRendererParams {
  weeks?: WeekColumn[];
  showOwnerGoal?: boolean;
  showDelta?: boolean;
}

/**
 * Metric cell: name on the first line, Owner · Goal · Δ WoW stacked beneath it.
 * Keeping the row's metadata inside the pinned first column frees ~380px of
 * horizontal space, so a nine-week window fits on screen without scrolling
 * right — the week values are what the WBR actually reads (spec R2.1, R9.2).
 */
const MetricNameCell: React.FC<MetricNameCellParams> = ({
  value,
  data,
  weeks,
  showOwnerGoal,
  showDelta,
}) => {
  const row = data as MetricTableRow | undefined;

  return (
    <div className="metric-name-cell">
      <Link href="#" variant="primary">
        {value}
      </Link>
      {row && (showOwnerGoal || showDelta) && (
        <div className="metric-name-cell__meta">
          {/* Labelled, matching the section header and the metric cards. */}
          {showOwnerGoal && row.owner && <span>Owner: {row.owner}</span>}
          {showOwnerGoal && <span>Goal: {formatGoal(row)}</span>}
          {/* Derived %DCRs rows have no trend of their own to compare. */}
          {showDelta && !row.derived && <MetricDelta row={row} weeks={weeks ?? []} />}
        </div>
      )}
    </div>
  );
};

/**
 * Value cell: rate values get goal-based text colouring (QuickSight-style,
 * never the cell background). No hover popover — clicking the cell opens
 * the callout side panel with full context.
 */
const MetricValueCell: React.FC<ICellRendererParams> = ({ value, data }) => {
  if (value === null || value === undefined) {
    return <span>–</span>;
  }

  const row = data as MetricTableRow | undefined;

  if (row?.isRate && typeof value === 'number') {
    // Rates come 0–100 from tableSelectors; goal from the metric definition.
    // RAG honors goalDirection: GT → green at/above goal; LT (lower is
    // better, e.g. aged backlog %) → green at/below goal. Amber within 10
    // points on the wrong side, red beyond. No goal → neutral (never guess).
    const goal = typeof row.goal === 'number' && row.goal > 0 ? row.goal : null;
    if (goal === null) {
      return <span>{value.toFixed(1)}</span>;
    }
    // Catalog goalDirection is GTE | LTE (metrics-contract-adoption R1.3).
    const lowerIsBetter = row.goalDirection === 'LTE';
    const missBy = lowerIsBetter ? value - goal : goal - value; // >0 = missing goal
    const statusClass =
      missBy <= 0 ? 'metric-value-good' : missBy <= 10 ? 'metric-value-warn' : 'metric-value-bad';
    return <span className={statusClass}>● {value.toFixed(1)}</span>;
  }

  return <span>{typeof value === 'number' ? value.toLocaleString() : value}</span>;
};

/** Cell selection surfaced in the callout side panel. */
interface SelectedCell {
  metric: string;
  weekLabel: string;
  value: string;
  callout: string;
}

/**
 * Local overrides for callouts posted this session, keyed metric×week.
 * TODO(backend): replace with the callout write API (metric_comments store,
 * keyed wbrId × week × metric per the execution plan); reads then come back
 * through getMetrics.
 */
type CalloutDrafts = Record<string, string>;

const calloutKey = (metric: string, weekLabel: string) => `${metric}#${weekLabel}`;

/**
 * All section instances share one viewport slot for the callout drawer, so
 * opening a panel must close any sibling section's panel. Each mounted
 * section registers a close callback here; opening calls the others.
 */
const panelCloseCallbacks = new Set<() => void>();
const closeSiblingPanels = (except: () => void) => {
  panelCloseCallbacks.forEach((cb) => {
    if (cb !== except) cb();
  });
};

/**
 * Placeholder for the callout AI-draft API: composes a starting draft from
 * the cell's context. TODO(backend): replace with the generate endpoint.
 */
function draftCalloutFromContext(cell: SelectedCell): string {
  return (
    `${cell.metric} at ${cell.value} for ${cell.weekLabel}. ` +
    'Root cause: <fill in>. Impact: <fill in>. Recovery plan and ETA: <fill in>.'
  );
}

export const MetricTableSection: React.FC<MetricTableSectionProps> = ({
  config,
  metricTypes,
  isSnapshot = false,
  respectsMetricGroup = true,
  showOwnerColumns = false,
  showTrend = true,
  network,
  rowSource = 'catalog',
}) => {
  const [expanded, setExpanded] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  // Callout panel: view mode, or edit mode with the in-progress text.
  const [isEditingCallout, setIsEditingCallout] = useState(false);
  const [calloutDraft, setCalloutDraft] = useState('');
  // Session-local posted callouts (metric×week) until the write API lands.
  const [postedCallouts, setPostedCallouts] = useState<CalloutDrafts>({});

  const dispatch = useAppDispatch();
  const activeNetwork = useAppSelector((state) => state.dashboard.activeNetwork);
  // R10: explicit network prop (multi-network Detailed Summary) wins over
  // the page-level activeNetwork.
  const scopeNetwork = network ?? activeNetwork;
  const reportingWeek = useAppSelector((state) => state.dashboard.reportingWeek);
  const lastXWeeks = useAppSelector((state) => state.dashboard.lastXWeeks);
  const forwardXWeeks = useAppSelector((state) => state.dashboard.forwardXWeeks);
  const filterQuery = useAppSelector((state) => state.dashboard.filterQuery);

  // Row-level filters (QuickSight top-bar parity): applied to DCRs before
  // aggregation, so all values recompute under the active filter chips.
  const dcrPredicate = useMemo(() => buildDcrPredicate(filterQuery), [filterQuery]);
  // Metric Group chips filter metric ROWS (the catalog), not DCR rows.
  const metricGroups = useMemo(() => selectedMetricGroups(filterQuery), [filterQuery]);

  // OTDThreshold parameter (QuickSight slider parity): the chip drives the
  // derived "% DCRs with OTD >= Threshold" rows; absent chip = default 95.
  // Recomputes client-side only — never a refetch (spec derived-otd R2).
  const otdThreshold = useMemo(() => {
    const token = filterQuery.tokens.find((t) => t.propertyKey === 'onTimeDeliveryThreshold');
    const value = token ? parseFloat(token.value) : NaN;
    return Number.isNaN(value) ? OTD_THRESHOLD_DEFAULT : value;
  }, [filterQuery]);

  // One callout drawer across all sections on the page (they share the same
  // fixed viewport slot) — register so a sibling opening its panel closes ours.
  const closePanel = useCallback(() => setSelectedCell(null), []);
  useEffect(() => {
    panelCloseCallbacks.add(closePanel);
    return () => {
      panelCloseCallbacks.delete(closePanel);
    };
  }, [closePanel]);

  // Week window: explicit tokens sliced from the getWeekNumbers universe
  // (spec metrics-window-caching — windows are always explicit; weeks are the
  // cache unit). Snapshot windows have no forward weeks and anchor on the
  // last COMPLETED week unless a reporting week is chosen.
  const { data: weekData } = useGetWeekNumbersQuery();
  const windowForMode = React.useCallback(
    (snapshot: boolean) => {
      if (!weekData) return null;
      const anchor =
        reportingWeek ??
        (snapshot ? weekBefore(weekData.weeks, weekData.currentWeek) : weekData.currentWeek);
      return buildDurationWindow(
        weekData.weeks,
        weekData.currentWeek,
        anchor,
        lastXWeeks,
        snapshot ? 0 : forwardXWeeks,
      );
    },
    [weekData, reportingWeek, lastXWeeks, forwardXWeeks],
  );
  const weekWindow = useMemo(() => windowForMode(isSnapshot), [windowForMode, isSnapshot]);
  // The same section in the other mode, warmed so the Live/Snapshot toggle does
  // not reload. Snapshot windows have no forward weeks and end at the last
  // completed week, so the two windows differ in both ends.
  const otherModeWindow = useMemo(() => windowForMode(!isSnapshot), [windowForMode, isSnapshot]);

  // Weeks are cached individually: every section on the page subscribes to the
  // same weeks (one request each), and changing the week controls fetches only
  // the weeks the new window gained (spec metrics-window-caching R1).
  const { data: apiResponse } = useMetricsWindow({
    id: WBR_DASHBOARD_ID,
    dataMode: isSnapshot ? 'SNAPSHOT' : 'LIVE',
    weeks: weekWindow?.durationRange,
    universe: weekData?.weeks,
    alsoWarm: otherModeWindow
      ? [
          {
            dataMode: isSnapshot ? ('LIVE' as const) : ('SNAPSHOT' as const),
            weeks: otherModeWindow.durationRange,
          },
        ]
      : undefined,
  });

  // Metric catalog (names, goals, groups) — one fetch per session, joined to
  // the per-record values by metricKey.
  const { data: definitionsResponse } = useGetMetricDefinitionsQuery({
    dashboardId: WBR_DASHBOARD_ID,
    reviewType: WBR_REVIEW_TYPE,
  });

  // All weeks render at once, grouped Historical | Current | Forward — the
  // old per-section period dropdown is gone (QuickSight shows the window
  // as grouped columns, not a picker).
  const visibleWeeks = useMemo(() => selectWeekColumns(apiResponse), [apiResponse]);

  const pivotedRows = useMemo((): MetricTableRow[] => {
    // Derived DSL Coverage section: rows come from DcrRecord.collectionOwner,
    // not the catalog — types/groups/config metric lists don't apply.
    if (rowSource === 'dslCoverage') {
      return selectDslCoverageRows(apiResponse, {
        network: scopeNetwork,
        predicate: dcrPredicate,
        // Catalog-first presentation: dsl_coverage_pct / avg_dcrs_per_dsl
        // definitions supply name/owner/goal when the catalog carries them.
        definitions: definitionsResponse?.definitions,
      });
    }
    const allRows = selectMetricTableRows(apiResponse, definitionsResponse?.definitions, {
      types: metricTypes,
      network: scopeNetwork,
      predicate: dcrPredicate,
      otdThreshold,
      metricGroups: respectsMetricGroup ? metricGroups : undefined,
    });
    // Section config lists specific metrics (view JSON); when provided and
    // matching, honour it — otherwise show everything the API returned.
    const configured = config.metrics?.length
      ? allRows.filter((r) => config.metrics.includes(r.metric))
      : allRows;
    return configured.length > 0 ? configured : allRows;
  }, [
    apiResponse,
    definitionsResponse,
    metricTypes,
    scopeNetwork,
    config.metrics,
    dcrPredicate,
    otdThreshold,
    metricGroups,
    respectsMetricGroup,
    rowSource,
  ]);

  const filteredBySearch = useMemo(() => {
    if (!search) return pivotedRows;
    const lower = search.toLowerCase();
    return pivotedRows.filter((row) => row.metric.toLowerCase().includes(lower));
  }, [pivotedRows, search]);

  const pagesCount = Math.max(1, Math.ceil(filteredBySearch.length / ROWS_PER_PAGE));
  // Clamp, don't just trust currentPage: filter/network changes can shrink
  // the rowset while the user sits on a later page — never show an empty
  // out-of-range page.
  const safePage = Math.min(currentPage, pagesCount);
  const paginatedRows = useMemo(() => {
    const start = (safePage - 1) * ROWS_PER_PAGE;
    return filteredBySearch.slice(start, start + ROWS_PER_PAGE);
  }, [filteredBySearch, safePage]);

  // Export: WYSIWYG columns over ALL pages of the section (offline
  // review needs the full section, not just the visible page — spec R3.2).
  // Percentages export as 1-decimal numbers, volumes as raw numbers.
  const exportColumns = useMemo((): ExportColumn<MetricTableRow>[] => {
    const cols: ExportColumn<MetricTableRow>[] = [
      { header: 'Metric', value: (r) => r.metric },
    ];
    if (showOwnerColumns) {
      cols.push({ header: 'Owner', value: (r) => r.owner });
      // Export keeps Owner/Goal/Δ WoW as discrete columns even though the grid
      // stacks them in the metric cell — a spreadsheet wants them sortable, so
      // Goal exports as a raw number rather than the display string.
      cols.push({ header: 'Goal', value: (r) => (r.goal > 0 ? r.goal : '') });
    }
    for (const week of visibleWeeks) {
      cols.push({
        header: `${week.label}${week.group === 'forward' ? ' (fwd)' : ''}`,
        value: (r) => {
          const v = r[week.key];
          if (typeof v !== 'number') return '';
          return r.isRate ? Number(v.toFixed(1)) : v;
        },
      });
    }
    cols.push({
      header: 'Δ WoW',
      value: (r) => {
        const { current, previous } = selectMetricTrend(r, visibleWeeks);
        if (current === null || previous === null) return '';
        return Number((current - previous).toFixed(1));
      },
    });
    return cols;
  }, [visibleWeeks, showOwnerColumns]);

  const exportFilename = useMemo(() => {
    const first = visibleWeeks[0]?.key ?? '';
    const last = visibleWeeks[visibleWeeks.length - 1]?.key ?? '';
    return `${config.id}-${first}-${last}`.replace(/[^\w-]+/g, '-');
  }, [config.id, visibleWeeks]);

  const columnDefs = useMemo((): (ColDef | ColGroupDef)[] => {
    // Owner, Goal and Δ WoW live INSIDE this cell, stacked under the metric
    // name, rather than in three columns of their own. They are row context
    // the reader glances at, while the week values are what the WBR discusses
    // — so the week columns get the horizontal space instead.
    const metricCol: ColDef = {
      headerName: 'Metric',
      field: 'metric',
      pinned: 'left',
      width: 320,
      cellRenderer: MetricNameCell,
      cellRendererParams: {
        weeks: visibleWeeks,
        showOwnerGoal: showOwnerColumns,
        showDelta: showTrend,
      },
      sortable: true,
    };

    // Trend sparkline. A visual aid — not sortable, not part of the callout
    // click flow (it has no `field`, so onCellClicked ignores it).
    const trendCols: ColDef[] = showTrend
      ? [
          {
            headerName: 'Trend',
            colId: 'trend',
            width: 215,
            sortable: false,
            cellRenderer: TrendCell,
            cellRendererParams: { weeks: visibleWeeks },
          },
        ]
      : [];

    // Group week columns under "1. Historical | 2. Current | 3. Forward"
    // header groups, QuickSight-style.
    //
    // The forward group starts COLLAPSED. Forward periods carry forecast and
    // commitment, which only the demand/volume metrics populate — every rate
    // row is empty there, so four columns of "–" push the weeks that do have
    // data off screen. Collapsed it costs one column instead of four, and the
    // group header carries AG Grid's expand toggle for when the forecast is
    // actually under discussion.
    //
    // The FIRST forward week stays visible when collapsed, deliberately: AG
    // Grid renders a group with zero visible children as nothing at all, which
    // would remove the toggle and leave no way back to the forward weeks. It is
    // also the most useful one to keep — next week's commitment.
    const groupOrder: PeriodGroup[] = ['historical', 'current', 'forward'];
    const weekCols: ColGroupDef[] = groupOrder
      .map((group) => ({
        headerName: PERIOD_GROUP_LABELS[group],
        openByDefault: false,
        children: visibleWeeks
          .filter((w) => w.group === group)
          .map((w, weekIndex): ColDef => ({
            headerName: w.label,
            field: w.key,
            width: 150,
            sortable: true,
            cellRenderer: MetricValueCell,
            // Only the forward group collapses; with a single forward week
            // there is nothing to hide, so no toggle appears.
            ...(group === 'forward' && weekIndex > 0
              ? { columnGroupShow: 'open' as const }
              : {}),
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) return '';
              return params.value;
            },
          })),
      }))
      .filter((g) => g.children.length > 0);

    return [metricCol, ...trendCols, ...weekCols];
  }, [visibleWeeks, showOwnerColumns, showTrend]);

  // The metric cell stacks name + meta, so rows need two lines whenever any
  // meta is shown; the sparkline needs the most room.
  const rowHeight = showTrend ? 80 : showOwnerColumns ? 62 : 44;

  const defaultColDef = useMemo((): ColDef => ({
    resizable: true,
    suppressMovable: true,
  }), []);

  const onSearchChange = useCallback(({ detail }: any) => {
    setSearch(detail.value);
    setCurrentPage(1);
  }, []);

  // Clicking a metric value cell opens the callout side panel with that
  // cell's context (metric × week × value + its WBR callout).
  const onCellClicked = useCallback(
    (event: CellClickedEvent) => {
      const field = event.colDef.field;
      const row = event.data as MetricTableRow | undefined;
      // Only week value cells open the callout panel — trend/delta columns
      // have no field, so they fall through the !field guard. Derived rows
      // (%DCRs≥threshold) have no single source value to annotate.
      if (!row || row.derived || !field || field === 'metric' || field === 'owner' || field === 'goal') {
        return;
      }
      const week = visibleWeeks.find((w) => w.key === field);
      if (!week) return;
      const raw = row[field];
      const value =
        raw === null || raw === undefined
          ? '–'
          : row.isRate
            ? `${Number(raw).toFixed(1)}%`
            : Number(raw).toLocaleString();
      // Callouts posted this session override what the API returned.
      const posted = postedCallouts[calloutKey(row.metric, week.label)];
      closeSiblingPanels(closePanel);
      setSelectedCell({
        metric: row.metric,
        weekLabel: week.label,
        value,
        callout: posted ?? row.callout,
      });
      setIsEditingCallout(false);
      setCalloutDraft('');
    },
    [visibleWeeks, postedCallouts, closePanel],
  );

  // Persist the draft locally and reflect it in the open panel.
  // TODO(backend): POST to the callout write API instead.
  const onPostCallout = useCallback(() => {
    if (!selectedCell) return;
    const text = calloutDraft.trim();
    if (!text) return;
    setPostedCallouts((prev) => ({
      ...prev,
      [calloutKey(selectedCell.metric, selectedCell.weekLabel)]: text,
    }));
    setSelectedCell({ ...selectedCell, callout: text });
    setIsEditingCallout(false);
  }, [selectedCell, calloutDraft]);

  return (
    <ExpandableSection
      variant="container"
      headerText={config.title.replace('{network}', demandCategoryDisplayName(scopeNetwork))}
      headerDescription={
        // Sections with per-row Owner/Goal columns skip the header line —
        // showing both is redundant (Owner belongs to metrics, not sections).
        showOwnerColumns
          ? undefined
          : (config.owner || config.goal)
            ? `${config.owner ? `Owner: ${config.owner}` : ''}${config.owner && config.goal ? ' · ' : ''}${config.goal ? `Goal: ${config.goal}` : ''}`
            : undefined
      }
      headerActions={
        <SpaceBetween direction="horizontal" size="xs">
          <Pagination
            currentPageIndex={safePage}
            pagesCount={pagesCount}
            onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          />
          <ExportMenu columns={exportColumns} rows={filteredBySearch} filename={exportFilename} />
        </SpaceBetween>
      }
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
    >
      <SpaceBetween direction="vertical" size="m">
        <Input
          value={search}
          placeholder="Find resources"
          onChange={onSearchChange}
          type="search"
        />

        {/* Self-explaining empty state (never AG Grid's bare "No Rows To
            Show"): zero rows here means network scoping and/or filter chips
            excluded every DCR before aggregation — name the cause and offer
            the way out. */}
        {pivotedRows.length === 0 ? (
          <Box textAlign="center" padding="l">
            <SpaceBetween direction="vertical" size="s">
              <Box variant="strong">
                No DCRs match {demandCategoryDisplayName(scopeNetwork)}
                {filterQuery.tokens.length > 0 ? ` with the ${filterQuery.tokens.length} active filter(s)` : ''} in the
                selected weeks.
              </Box>
              <Box color="text-body-secondary">
                The network may have no data for this window, or a filter value excludes all rows.
              </Box>
              {filterQuery.tokens.length > 0 && (
                <Button
                  variant="normal"
                  onClick={() => dispatch(filterQueryChanged({ tokens: [], operation: 'and' }))}
                >
                  Clear filters
                </Button>
              )}
            </SpaceBetween>
          </Box>
        ) : (
          <div
            className="ag-theme-alpine metric-table-grid"
            style={{ height: `${Math.max(200, paginatedRows.length * (rowHeight + 4) + 56)}px` }}
          >
            <AgGridReact
              rowData={paginatedRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"
              suppressCellFocus={true}
              headerHeight={40}
              rowHeight={rowHeight}
              onCellClicked={onCellClicked}
            />
          </div>
        )}

        {/* Callout drawer: fixed to the right viewport edge when a metric
            value cell is clicked (Cloudscape help-panel pattern). Authoring
            comes later — shows the cell's context and its WBR callout. */}
        {selectedCell && (
            <div className="metric-callout-panel">
              <div className="metric-callout-panel__header">
                <Header
                  variant="h3"
                  actions={
                    <Button
                      variant="icon"
                      iconName="close"
                      ariaLabel="Close callout panel"
                      onClick={() => setSelectedCell(null)}
                    />
                  }
                >
                  Callout
                </Header>
              </div>
              <div className="metric-callout-panel__body">
                <SpaceBetween direction="vertical" size="s">
                  <div>
                    <div className="metric-callout-panel__label">Metric</div>
                    <div>{selectedCell.metric}</div>
                  </div>
                  <div>
                    <div className="metric-callout-panel__label">Week</div>
                    <div>{selectedCell.weekLabel}</div>
                  </div>
                  <div>
                    <div className="metric-callout-panel__label">Value</div>
                    <div>{selectedCell.value}</div>
                  </div>
                  <div>
                    <div className="metric-callout-panel__label">Callout</div>
                    {isEditingCallout ? (
                      <SpaceBetween direction="vertical" size="xs">
                        <Textarea
                          value={calloutDraft}
                          onChange={({ detail }) => setCalloutDraft(detail.value)}
                          placeholder="What happened, impact, and recovery plan…"
                          rows={6}
                        />
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button variant="primary" onClick={onPostCallout} disabled={!calloutDraft.trim()}>
                            Post callout
                          </Button>
                          {/* TODO(backend): wire to the AI-draft endpoint. */}
                          <Button
                            variant="normal"
                            iconName="gen-ai"
                            onClick={() => setCalloutDraft(draftCalloutFromContext(selectedCell))}
                          >
                            Regenerate
                          </Button>
                          <Button variant="link" onClick={() => setIsEditingCallout(false)}>
                            Cancel
                          </Button>
                        </SpaceBetween>
                      </SpaceBetween>
                    ) : (
                      <SpaceBetween direction="vertical" size="xs">
                        <div>{selectedCell.callout || 'No callout recorded for this metric.'}</div>
                        <Button
                          variant="normal"
                          iconName="edit"
                          onClick={() => {
                            setCalloutDraft(selectedCell.callout || draftCalloutFromContext(selectedCell));
                            setIsEditingCallout(true);
                          }}
                        >
                          {selectedCell.callout ? 'Edit callout' : 'Add callout'}
                        </Button>
                      </SpaceBetween>
                    )}
                  </div>
                </SpaceBetween>
              </div>
            </div>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
};
